import { ChangeEvent } from "react";
import {
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  FormGroup,
  FormControlLabel,
  SvgIcon,
  makeStyles,
  createStyles,
  Theme,
  FormControlLabelProps,
} from "@material-ui/core";
import { InputWrapper, InputWrapperProps } from "~components/InputWrapper";
import { Tick } from "~components/Icons";
import RemoveIcon from "@material-ui/icons/Remove";

type Item = {
  label: string;
  value: number | string;
};

type CheckboxGroupProps = {
  items: Item[];
  value: Item["value"][];
  onChange: (val: Item["value"][]) => void;
} & InputWrapperProps;

type CheckboxBaseProps = {} & MuiCheckboxProps;
type CheckboxProps = {
  indeterminate?: boolean;
  checkboxBaseProps?: CheckboxBaseProps;
} & Omit<FormControlLabelProps, "control">;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    checkbox: {
      padding: 0,

      "&:hover": {
        backgroundColor: "transparent !important",
      },

      "&:disabled": {
        background: theme.palette.background.default,
      },
    },
    labelRoot: {
      margin: 0,

      "&:last-child": {
        marginBottom: 0,
      },
    },
    label: {
      fontSize: 16,
      fontWeight: "normal",
      lineHeight: 1,
      paddingTop: 1,
      color: theme.palette.dark.main,
      verticalAlign: "middle",
      cursor: "pointer",
      display: "inline-block",

      marginLeft: theme.spacing(2),
    },
    icon: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",

      backgroundColor: "white",
      height: 24,
      width: 24,

      color: theme.palette.primary.main,
      fontSize: 16,

      borderRadius: 4,

      boxShadow: `0 0 0 1px ${theme.palette.blueGrey.main}`,

      "input:enabled:hover ~ &": {
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}, 0 4px 8px 0 rgba(26, 99, 171, 0.16), 0 0 1px 0 rgba(26, 99, 171, 0.08)`,
      },
      "input:enabled:focus ~ &": {
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}, 0 4px 8px 0 rgba(26, 99, 171, 0.16), 0 0 1px 0 rgba(26, 99, 171, 0.08)`,
      },
    },
  }),
);

export const CheckboxBase = (props: CheckboxBaseProps) => {
  const classes = useStyles();

  return (
    <MuiCheckbox
      disableFocusRipple
      className={classes.checkbox}
      checkedIcon={
        <span className={classes.icon}>
          <SvgIcon component={Tick} />
        </span>
      }
      indeterminateIcon={
        <span className={classes.icon}>
          <RemoveIcon />
        </span>
      }
      icon={<span className={classes.icon} />}
      {...props}
    />
  );
};

export const Checkbox = ({ indeterminate, checkboxBaseProps, ...props }: CheckboxProps) => {
  const classes = useStyles();
  return (
    <FormControlLabel
      control={<CheckboxBase indeterminate={indeterminate} {...checkboxBaseProps} />}
      {...props}
      classes={{ root: classes.labelRoot, label: classes.label }}
    />
  );
};

export const CheckboxGroup = ({
  items,
  id,
  label,
  error,
  onChange,
  value,
  ...rest
}: CheckboxGroupProps) => {
  const customId = `radioGroup-${id ?? label}`;
  const classes = useStyles();

  const handleChange = (e: ChangeEvent<{}>, checked: boolean) => {
    if (onChange) {
      if (checked) {
        onChange([...value, (e as ChangeEvent<HTMLInputElement>).target.value]);
      } else {
        onChange(value.filter((v) => v !== (e as ChangeEvent<HTMLInputElement>).target.value));
      }
    }
  };

  return (
    <InputWrapper id={customId} label={label} error={error}>
      <FormGroup {...rest} id={customId}>
        {items.map((item) => (
          <FormControlLabel
            key={item.value}
            control={<CheckboxBase />}
            {...item}
            {...(value && { checked: value.includes(item.value) })}
            onChange={handleChange}
            classes={{ root: classes.labelRoot, label: classes.label }}
          />
        ))}
      </FormGroup>
    </InputWrapper>
  );
};
